import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from "react";
import type {
  LiveUpdateState,
  LiveUpdateSubscription,
  LiveUpdatesClient,
} from "./liveUpdatesClient";

export const LiveUpdatesClientContext = createContext<LiveUpdatesClient | null>(
  null,
);

const useClient = (): LiveUpdatesClient => {
  const client = useContext(LiveUpdatesClientContext);
  if (!client) {
    throw new Error(
      "Live-update hooks must be used inside LiveUpdatesProvider",
    );
  }
  return client;
};

export const useLiveUpdateStatus = (): LiveUpdateState => {
  const client = useClient();
  return useSyncExternalStore(
    client.subscribeToState,
    client.getState,
    client.getState,
  );
};

export interface UseLiveUpdatesResult {
  state: LiveUpdateState;
  retry(): void;
}

export const useLiveUpdates = (
  subscription: LiveUpdateSubscription,
  enabled = true,
): UseLiveUpdatesResult => {
  const client = useClient();
  const { key } = subscription;
  const latestSubscription = useRef(subscription);

  useEffect(() => {
    latestSubscription.current = subscription;
  }, [subscription]);

  useEffect(() => {
    if (!enabled) return;
    return client.subscribe({
      key,
      shouldRefresh: (event) => latestSubscription.current.shouldRefresh(event),
      refresh: () => latestSubscription.current.refresh(),
    });
  }, [client, enabled, key]);

  const state = useLiveUpdateStatus();
  return { state, retry: client.retry };
};

export const useLiveUpdatesClientForDiagnostics = (): LiveUpdatesClient =>
  useClient();